<script setup lang="ts">
import { computed } from 'vue'
import { ChevronRight, ChevronUp, Layers, Maximize, Minimize, Sparkles } from 'lucide-vue-next'
import type { FeedPreset, IRefVideoItem } from '../../types'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'

const { t } = useI18n()

const props = defineProps<{
  progress: number
  data: IRefVideoItem
  isFullscreen: boolean
  preset: FeedPreset
}>()

const emit = defineEmits<{
  (e: 'toggle-fullscreen', isFullscreen: boolean): void
  (e: 'toggle-info-popup', show: boolean): void
  (e: 'episodes-tap'): void
  (e: 'primary-action'): void
}>()

const isRecommendMode = computed(() => props.preset === 'recommend')
const isCreationMode = computed(() => props.preset === 'creation')
</script>

<template>
  <div class="h-12 relative" style="padding-bottom: env(safe-area-inset-bottom, 0px)">
    <!-- 进度条 -->
    <div class="absolute left-0 right-0 top-0 -translate-y-full z-10 bg-gray-950">
      <div class="w-full h-px bg-white/20 rounded-full overflow-hidden">
        <div class="h-full bg-white/60 transition-all duration-75 ease-linear" :style="{ width: `${progress}%` }" />
      </div>
    </div>
    <!-- 首页推荐 -->
    <template v-if="isRecommendMode">
      <router-link
        :to="`/theater/detail/${data.scriptIdKey}?episodeId=${data.episodeId}`"
        class="shrink-0 bg-gray-950 h-12 w-full flex flex-col gap-4 relative"
      >
        <div class="absolute top-0 -translate-y-full p-4 flex flex-col gap-4">
          <div class="font-bold uppercase">{{ data?.title }}</div>
          <div class="flex items-center gap-2">
            <span
              class="backdrop-blur-md bg-white/40 rounded-md px-1.5 py-1 text-xs text-white"
              v-for="tag in data.tags"
              :key="tag"
              >{{ tag }}</span
            >
          </div>
        </div>

        <div
          class="cursor-pointer hover:opacity-80 px-4 py-3 flex items-center justify-between border-t border-white/10 gap-4"
        >
          <div class="flex items-center gap-2">
            <Layers :size="18" /> {{ t('home.page.watchFullStory', { episodes: data.totalEpisodes }) }}
          </div>
          <ChevronRight :size="20" />
        </div>
      </router-link>
    </template>
    <!-- 首页创作 -->
    <template v-else-if="isCreationMode">
      <div class="flex h-12 flex-col bg-gray-950 relative">
        <div class="absolute top-0 w-full -translate-y-full p-4 flex flex-col gap-3">
          <!-- <div class="font-bold uppercase line-clamp-2">{{ data.title }}</div> -->
          <!-- <div class="line-clamp-2 text-sm text-white/80">{{ data.description }}</div> -->
          <div class="flex items-center gap-2">
            <span
              class="backdrop-blur-md bg-white/40 rounded-md px-1.5 py-1 text-xs text-white"
              v-for="tag in data.tags"
              :key="tag"
              >{{ tag }}</span
            >
          </div>
        </div>

        <div @click.stop="emit('primary-action')" class="h-12 px-4 flex items-center justify-center gap-3">
          <div class="flex items-center gap-2 flex-1">
            <Icon icon="solar:notes-minimalistic-bold-duotone" class="text-2xl" />
            {{ t('component.feed.remixAction') }}
          </div>
          <ChevronRight class="transition-transform" />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex h-12 flex-col bg-gray-950 relative">
        <div class="w-full flex flex-col absolute top-0 -translate-y-full">
          <div class="p-4 flex flex-col gap-4 max-w-4/5">
            <div class="uppercase text-sm text-justify transition-all duration-200 ease-in-out">
              {{ data.description }}
            </div>
          </div>
        </div>
        <div class="h-12 px-2 flex items-center justify-center gap-4 cursor-pointer">
          <div
            @click.stop="emit('episodes-tap')"
            class="px-3 bg-white/10 h-9 rounded-xl flex items-center justify-between gap-4 flex-1"
          >
            <div class="flex items-center gap-2 flex-1">
              <Layers :size="18" />
              {{ t('component.playletViewer.selectEpisode') }} ● {{ t('enum.taskStatus.success') }} ●
              {{ t('component.playletViewer.totalEpisodes', { num: data.totalEpisodes }) }}
            </div>
            <ChevronUp class="transition-transform" />
          </div>
          <Minimize v-if="isFullscreen" :size="24" @click="emit('toggle-fullscreen', false)" />
          <Maximize v-else :size="24" @click="emit('toggle-fullscreen', true)" />
        </div>
      </div>
    </template>
  </div>
</template>
